/* eslint-disable no-use-before-define */

import { Result } from '@easeltv/fail';
import { ZodType, ZodTypeDef } from 'zod';

import { ApiFail } from '../errors';

/* -------------------------- NonValidatedResponse -------------------------- */

/**
 * NonValidatedResponse
 *
 * Parsed response returned from fetch request before the response has been validated
 */
export type NonValidatedResponse = {
    body: unknown;
    status: number;
};

/* -------------------------- RequestBuilderOptions ------------------------- */

/**
 * RequestBuilderOptions
 *
 * Optional properties used to create an instance of RequestBuilder
 */
export type RequestBuilderOptions<
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
> = {
    validator: ZodType<Output, Def, Input>;
    body?: Record<string, string>;
    headers?: Record<string, string>;
    params?: Record<string, string>;
    queries?: Record<string, string>;
    abortController?: AbortController;
};

/* ------------------------------ RequestMethod ----------------------------- */

/**
 * RequestMethod
 *
 * Enum containing the possible fetch request methods
 */
export enum RequestMethod {
    GET = 'GET',
    HEAD = 'HEAD',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE',
    OPTIONS = 'OPTIONS',
    PATCH = 'PATCH',
}

/* ----------------------------- RequestOptions ----------------------------- */

/**
 * RequestOptions
 *
 * Options to be passed to the fetch request
 */
export type RequestOptions = {
    queries?: Record<string, string>;
    params?: Record<string, string>;
};

/* ----------------------------- RequestResponse ---------------------------- */

/**
 * RequestResponse
 *
 * Helper type representing all possible response types
 */
export type RequestResult<Output, F extends ApiFail = ApiFail> = Result<
    ValidatedResponse<Output>,
    F
>;

/* ------------------------------- SchemaType ------------------------------- */

/**
 * SchemaType
 *
 * Generic type representing a Zod Schema / Type / Effect that we can call safeParse on
 */
export type SchemaType<
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
> = ZodType<Output, Def, Input>;

/* ---------------------------- ValidatedResponse --------------------------- */

/**
 * ValidatedResponse
 *
 * Parsed and validated response returned from fetch request
 */
export type ValidatedResponse<T> = {
    body: T;
    status: number;
};

export type BaseValidator = ZodTypeDef;
